import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { StudentsScope, SchoolScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import fillMonths from 'teacher-ui-client/utils/student-events/fill-months'

export interface StorylandsTotalsResponse {
  storylands_totals_summary_by_grade: {
    grade_position: number
    data: [{ category: 'total_count'; count: number }, { category: 'average_score'; average: number }]
  }[]
  storylands_totals_by_grade_by_month: {
    grade_position: number
    months: {
      month: string
      count: number
    }[]
  }[]
}

export interface StorylandsTotalsDataItem {
  grade_position: number
  summary: {
    total_count: number
    average_score: number
  }
  months: {
    month: string
    count: number
  }[]
}

export async function storylandsTotals(
  this: StudentEventsService,
  product: LegacyProductRe,
  statsScope: StudentsScope | SchoolScope,
  period: NormalizedInterval,
): Promise<Nullable<StorylandsTotalsDataItem[]>> {
  if (isEmpty(statsScope.ids)) return Promise.resolve(null)

  const url = this.buildUrl('storylands_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<StorylandsTotalsResponse>(url, query)
  if (!data) return null

  const { storylands_totals_summary_by_grade: summaryData, storylands_totals_by_grade_by_month: monthData } = data
  if (monthData.length === 0) return null

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(
  summaryData: StorylandsTotalsResponse['storylands_totals_summary_by_grade'],
  monthData: StorylandsTotalsResponse['storylands_totals_by_grade_by_month'],
  period: NormalizedInterval,
) {
  const sortedSummary = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummary
    .map((summary) => {
      const gradeMonthData = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!gradeMonthData) return null

      return {
        grade_position: summary.grade_position,
        summary: {
          total_count: summary.data.find((summaryItem) => summaryItem.category === 'total_count')!.count,
          average_score: summary.data.find((summaryItem) => summaryItem.category === 'average_score')!.average,
        },
        months: fillMonths(gradeMonthData.months, period, {
          count: 0,
        }),
      }
    })
    .filter((data) => data !== null)
}
